// Клиент API кабинета. Сессия живёт в httpOnly-куке, поэтому запросы
// отправляются с credentials, а токены в браузере не хранятся.

const BASE = '/dashboard/v1'

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(BASE + path, {
    ...init,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
  })

  const text = await response.text()
  const body = text ? JSON.parse(text) : {}
  if (!response.ok) {
    throw new ApiError(response.status, body.error ?? `Ошибка ${response.status}`)
  }
  return body as T
}

export interface User {
  id: string
  email: string
  name: string
  is_superadmin: boolean
  orgs?: { id: string; role: string }[]
}

export interface App {
  id: string
  name: string
  org_id: string
  created_at: string
}

export interface Channel {
  channel: string
  enabled: boolean
  config: Record<string, string>
  credential?: { kind: string; fingerprint: string; expires_at?: string; created_at: string }
}

export interface ApiKey {
  id: string
  name: string
  prefix: string
  kind: string
  created_at: string
  last_used_at?: string
}

export interface AppDetail extends App {
  channels: Channel[]
  settings: Record<string, unknown>
}

export interface Subscription {
  id: string
  user_id: string
  external_id?: string
  type: string
  token: string
  enabled: boolean
  notification_types: number
  device_model?: string
  device_os?: string
  last_active_at?: string
  invalid_at?: string
  sent_30d: number
  clicked_30d: number
  failed_30d: number
  last_sent_at?: string
}

export interface NotificationRow {
  id: string
  name: string
  status: string
  contents: Record<string, string>
  headings: Record<string, string>
  recipients: number
  successful: number
  failed: number
  errored: number
  received: number
  converted: number
  created_at: string
}

export interface Stats {
  audience: { total: number; subscribed: number; invalid: number }
  daily: { day: string; platform: string; type: string; count: number }[]
}

export interface DeliveryDaily {
  day: string
  sent: number
  devices: number
  clicked: number
  failed: number
  received: number
}

export interface DeliverySummary {
  since: string
  approximate: boolean
  sent: number
  devices: number
  received: number
  clicked: number
  failed: number
  by_platform: { platform: string; sent: number; devices: number; clicked: number; failed: number }[]
  daily: DeliveryDaily[]
}

export interface TopDevice {
  subscription_id: string
  external_id?: string
  type: string
  sent: number
  clicked: number
}

export interface DeliveryReport {
  days: number
  retention_days: number
  summary: DeliverySummary
  top_devices: TopDevice[]
}

export interface LogEntry {
  id: number
  ts: string
  type: string
  platform: string
  provider: string
  code?: string
  raw?: string
  notification_id?: string
  subscription_id?: string
  title?: string
}

export interface Diagnostics {
  credentials: {
    channel: string
    enabled: boolean
    has_credential: boolean
    kind?: string
    expires_at?: string
    days_left?: number
  }[]
  recent_errors: { provider: string; platform: string; code: string; raw: string; count: number; ts: string }[]
  hints: string[]
}

export interface PlatformTotals {
  orgs: number
  suspended_orgs: number
  apps: number
  users: number
  subscriptions: number
  subscribed: number
  sent_30d: number
  failed_30d: number
}

export interface OrgStats {
  id: string
  name: string
  status: string
  created_at: string
  apps: number
  members: number
  subscriptions: number
  subscribed: number
  sent_30d: number
  clicked_30d: number
  failed_30d: number
  last_activity?: string
}

export interface OrgMember {
  id: string
  email: string
  name: string
  role: string
  is_superadmin: boolean
  status: string
  last_login_at?: string
}

export interface AdminChannel {
  channel: string
  enabled: boolean
  config: Record<string, string>
  has_credential: boolean
  credential_kind?: string
  fingerprint?: string
  expires_at?: string
}

export interface AdminApp {
  id: string
  name: string
  created_at: string
  settings: Record<string, unknown>
  channels: AdminChannel[]
  api_keys: number
  subscriptions: number
  subscribed: number
  invalid: number
  sent_30d: number
  failed_30d: number
  notifications: number
  last_send_at?: string
}

export interface AuditRecord {
  id: number
  actor_type: string
  action: string
  target: string
  ip: string
  created_at: string
}

export interface OrgDetail {
  org: { id: string; name: string; status: string; created_at: string }
  members: OrgMember[]
  apps: AdminApp[]
  audit: AuditRecord[]
}

export const api = {
  settings: () => request<{ registration_open: boolean }>('/auth/settings'),
  register: (email: string, password: string, name: string) =>
    request<{ user: User }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    }),
  login: (email: string, password: string) =>
    request<{ user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<unknown>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/me'),

  apps: () => request<{ apps: App[] }>('/apps'),
  createApp: (name: string, appId?: string) =>
    request<{ app: App; api_key: string }>('/apps', {
      method: 'POST',
      body: JSON.stringify(appId ? { name, app_id: appId } : { name }),
    }),
  changeAppId: (appId: string, newAppId: string) =>
    request<{ id: string; changed: boolean; previous_id?: string }>(`/apps/${appId}/app-id`, {
      method: 'PUT',
      body: JSON.stringify({ app_id: newAppId }),
    }),
  app: (id: string) => request<AppDetail>(`/apps/${id}`),

  saveChannel: (appId: string, channel: string, payload: unknown) =>
    request<{ ok: boolean }>(`/apps/${appId}/channels/${channel}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  keys: (appId: string) => request<{ keys: ApiKey[] }>(`/apps/${appId}/keys`),
  revokeKey: (appId: string, keyId: string) =>
    request<{ revoked: string }>(`/apps/${appId}/keys/${keyId}`, { method: 'DELETE' }),
  deleteApp: (appId: string, confirm: string) =>
    request<{ deleted: string }>(`/apps/${appId}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirm }),
    }),
  createKey: (appId: string, name: string) =>
    request<{ key: string }>(`/apps/${appId}/keys`, { method: 'POST', body: JSON.stringify({ name }) }),

  subscriptions: (appId: string) =>
    request<{ subscriptions: Subscription[] }>(`/apps/${appId}/subscriptions?limit=100`),
  notifications: (appId: string) =>
    request<{ notifications: NotificationRow[] }>(`/apps/${appId}/notifications?limit=50`),
  stats: (appId: string) => request<Stats>(`/apps/${appId}/stats`),
  delivery: (appId: string, days: number) =>
    request<DeliveryReport>(`/apps/${appId}/delivery?days=${days}`),
  log: (appId: string, params: Record<string, string | number | undefined>) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    return request<{ entries: LogEntry[]; next_before: number }>(`/apps/${appId}/log?${query}`)
  },
  diagnostics: (appId: string) => request<Diagnostics>(`/apps/${appId}/diagnostics`),
  sendPush: (appId: string, payload: Record<string, unknown>) =>
    request<{ id: string; recipients: number; warning?: string }>(`/apps/${appId}/notifications`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  testPush: (appId: string, subscriptionId: string, title: string, body: string) =>
    request<{ notification_id: string }>(`/apps/${appId}/test-push`, {
      method: 'POST',
      body: JSON.stringify({ subscription_id: subscriptionId, title, body }),
    }),

  adminSettings: () =>
    request<{ registration_open: boolean; totals: PlatformTotals }>('/admin/settings'),
  setRegistrationOpen: (open: boolean) =>
    request<{ registration_open: boolean }>('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify({ registration_open: open }),
    }),
  orgs: () => request<{ orgs: OrgStats[] }>('/admin/orgs'),
  org: (id: string) => request<OrgDetail>(`/admin/orgs/${id}`),
  setOrgStatus: (id: string, status: 'active' | 'suspended') =>
    request<{ id: string; status: string }>(`/admin/orgs/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),
  audit: () => request<{ audit: AuditRecord[] }>('/admin/audit'),
}

// Файл креда уезжает на сервер в base64: бинарные .p8 и .p12 через JSON иначе не передать.
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}
